/**
 * Harcama Faturası (GENEL) — her marka için aylık mecra harcaması ızgarası
 *
 * `/faturalama` sayfasının markadan bağımsız kardeşi. O sayfa Yataş grubunun
 * fatura biçimine göre yazıldı ve öyle KALIYOR: oranlar (Google %4,5, Meta
 * %2,65, Pinterest/TikTok %15) koda gömülü, mağaza ziyareti kampanyaları
 * "Lokal" satırına ayrılıyor, satırlar Branding/Performance diye ikiye
 * bölünüyor. Bunların hiçbiri genel bir müşteride doğru değil.
 *
 * Buradaki sayfa hiçbir şey VARSAYMAZ:
 *  · Satır = mecra, sütun = ay. Branding/Performance ayrımı YOK (o kural
 *    Yataş'ın kampanya adlandırmasına dayanıyor, başka markada yanlış sonuç
 *    verir). Kampanya kırılımı hücreye tıklanınca açılır.
 *  · Lokal ayrıştırma YOK.
 *  · Ücret/komisyon oranı yok — kullanıcı tanımlarsa uygulanır. Ayarlar marka
 *    bazında `/api/billing/fee-settings` içinde saklanır, `/faturalama` sayfası
 *    bu ayarları OKUMAZ (iki sayfa birbirini etkilemez).
 *
 * Rakamlar DB'den değil, `/api/billing/live-spend` üzerinden platform
 * API'lerinden anlık gelir ve markanın TÜM aktif reklam hesaplarını toplar.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  ChevronLeft, ChevronRight, RefreshCw, Loader2,
  AlertTriangle, Download, Save, Plug, X, Plus, Trash2, Percent, Settings2,
} from 'lucide-react';

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const hdr = (brandId) => ({
  'X-Brand-ID': brandId,
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const AY_ADLARI = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const AY_KISA = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const AY_SAYISI = 6;

/* Sidebar'ın kullandığı logo dosyaları — public/ altında duruyorlar */
const PLATFORM_LOGO = {
  GOOGLE_ADS: '/google_ads-512.webp',
  META_ADS: '/6033716.png',
  PINTEREST_ADS: '/pinterestlogopng.png',
  TIKTOK_ADS: '/tik-tokiconss.png',
  CRITEO_ADS: '/CRcriteopng.png',
  RTB_HOUSE: '/RTBhouseiconPNG.png',
};

const PLATFORM_BASLIK = {
  GOOGLE_ADS: 'Google Ads',
  META_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest',
  TIKTOK_ADS: 'TikTok Ads',
  CRITEO_ADS: 'Criteo',
  RTB_HOUSE: 'RTB House',
};

/** Sık kullanılan oranlar — ayar panelinde tek tıkla doldurmak için, ZORUNLU DEĞİL. */
const HAZIR_ORANLAR = [
  { etiket: 'Operating fee 4.5%', yuzde: 4.5, tur: 'dahil', brut: false },
  { etiket: 'Commission 10%', yuzde: 10, tur: 'satir', brut: false },
  { etiket: 'Withholding 15% (gross-up)', yuzde: 15, tur: 'satir', brut: true },
];

const num = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n, currency = 'TRY') => {
  const v = Number(n || 0);
  try {
    return v.toLocaleString('en-US', { style: 'currency', currency, maximumFractionDigits: 2 });
  } catch {
    return `${num(v)} ${currency}`;
  }
};
const yuzdeYaz = (oran) => `${(Number(oran || 0) * 100).toLocaleString('en-US', { maximumFractionDigits: 3 })}%`;
const ayAnahtar = (y, m) => `${y}-${String(m).padStart(2, '0')}`;

/**
 * Bir kalemin üstüne binen ücret.
 * `brut` = brütleştirme (stopaj mantığı): tutar / (1 − oran) − tutar.
 * Düz çarpımla karıştırma — %15 stopajda aradaki fark kalem başına ~%2,65'tir.
 */
const ucretTutari = (tutar, ucret) => {
  if (!ucret || !ucret.aktif || !ucret.oran) return 0;
  const t = Number(tutar || 0);
  return ucret.brut ? t / (1 - ucret.oran) - t : t * ucret.oran;
};

const BOS_AYAR = { mecralar: {}, genel: [] };

/* ── Ana sayfa ──────────────────────────────────────────────────────── */

export default function BillingSpendGeneral() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId') || '';

  const now = new Date();
  // Fatura kontrolü kapanmış ay için yapılır → ızgaranın SON sütunu bir önceki ay.
  const gecenAy = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [endYear, setEndYear] = useState(gecenAy.getFullYear());
  const [endMonth, setEndMonth] = useState(gecenAy.getMonth() + 1);

  const [platforms, setPlatforms] = useState([]);
  const [platformsLoading, setPlatformsLoading] = useState(false);
  const [cells, setCells] = useState({});     // `${ayAnahtar}|${platform}` -> {loading,error,data}
  const [ayarlar, setAyarlar] = useState(BOS_AYAR);
  const [ayarPanel, setAyarPanel] = useState(false);
  const [taslak, setTaslak] = useState(null); // panel açıkken düzenlenen kopya
  const [ayarKaydediliyor, setAyarKaydediliyor] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [detay, setDetay] = useState(null);   // {platformKey, ay}

  const reqIdRef = useRef(0); // marka/ay değişince eski yanıtların state'i ezmesini engeller

  /* Izgara sütunları — eskiden yeniye 6 ay */
  const aylar = useMemo(() => {
    const out = [];
    for (let i = AY_SAYISI - 1; i >= 0; i--) {
      const d = new Date(endYear, endMonth - 1 - i, 1);
      out.push({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        key: ayAnahtar(d.getFullYear(), d.getMonth() + 1),
        kisa: AY_KISA[d.getMonth()],
        uzun: `${AY_ADLARI[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return out;
  }, [endYear, endMonth]);

  /* ── Veri çekimi ─────────────────────────────────────────────── */

  const fetchAll = useCallback(async () => {
    if (!brandId) return;
    const reqId = ++reqIdRef.current;
    setPageError(null);
    setPlatformsLoading(true);
    setCells({});
    setDetay(null);
    setPlatforms([]);

    // Ücret ayarları ay bazlı değil — bir kez okunur.
    try {
      const res = await fetch(`${API_BASE}/api/billing/fee-settings`, {
        credentials: 'include',
        headers: hdr(brandId),
      });
      if (res.ok) {
        const json = await res.json();
        if (reqId !== reqIdRef.current) return;
        setAyarlar({ mecralar: json.settings?.mecralar || {}, genel: json.settings?.genel || [] });
      }
    } catch (e) {
      console.error('[billing-genel] ücret ayarları okunamadı', e);
    }

    let connected = [];
    try {
      const res = await fetch(`${API_BASE}/api/billing/platforms`, {
        credentials: 'include',
        headers: hdr(brandId),
      });
      if (!res.ok) throw new Error(`Could not load channel list (${res.status})`);
      const json = await res.json();
      if (reqId !== reqIdRef.current) return;
      setPlatforms(json.platforms || []);
      connected = (json.platforms || []).filter((p) => p.connected);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setPageError(e.message);
      setPlatformsLoading(false);
      return;
    }
    setPlatformsLoading(false);

    const bekleyen = {};
    for (const ay of aylar) for (const p of connected) bekleyen[`${ay.key}|${p.key}`] = { loading: true };
    setCells(bekleyen);

    // AY AY, en yeniden eskiye. Ay içinde mecralar paralel; aylar sıralı, çünkü
    // 6 ay × tüm mecralar aynı anda gidince Meta/TikTok hız sınırına takılıyor.
    const sirali = [...aylar].reverse();
    for (const ay of sirali) {
      if (reqId !== reqIdRef.current) return;
      await Promise.all(
        connected.map(async (p) => {
          const hucre = `${ay.key}|${p.key}`;
          try {
            const params = new URLSearchParams({ platform: p.key, year: String(ay.year), month: String(ay.month) });
            const res = await fetch(`${API_BASE}/api/billing/live-spend?${params}`, {
              credentials: 'include',
              headers: hdr(brandId),
            });
            const json = await res.json().catch(() => ({}));
            if (reqId !== reqIdRef.current) return;
            if (!res.ok) throw new Error(json.error || `Error (${res.status})`);
            setCells((prev) => ({ ...prev, [hucre]: { loading: false, data: json } }));
          } catch (e) {
            if (reqId !== reqIdRef.current) return;
            setCells((prev) => ({ ...prev, [hucre]: { loading: false, error: e.message } }));
          }
        })
      );
    }
  }, [brandId, aylar]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Ücret ayarları ──────────────────────────────────────────── */

  const panelAc = () => {
    // Panel taslak üzerinde çalışır; Kaydet'e basılmadan ızgara değişmez.
    setTaslak({
      mecralar: Object.fromEntries(
        Object.entries(ayarlar.mecralar || {}).map(([k, v]) => [k, { ...v, yuzde: Number((v.oran * 100).toFixed(4)) }])
      ),
      genel: (ayarlar.genel || []).map((g) => ({ ...g, yuzde: Number((g.oran * 100).toFixed(4)) })),
    });
    setAyarPanel(true);
  };

  const mecraAyarla = (key, patch) =>
    setTaslak((t) => ({
      ...t,
      mecralar: {
        ...t.mecralar,
        [key]: { aktif: true, tur: 'dahil', brut: false, etiket: '', yuzde: 0, ...(t.mecralar[key] || {}), ...patch },
      },
    }));

  const mecraSil = (key) =>
    setTaslak((t) => {
      const { [key]: _cikan, ...kalan } = t.mecralar;
      return { ...t, mecralar: kalan };
    });

  const ayarKaydet = useCallback(async () => {
    if (!brandId || !taslak) return;
    setAyarKaydediliyor(true);
    try {
      const settings = {
        mecralar: Object.fromEntries(
          Object.entries(taslak.mecralar)
            .filter(([, v]) => Number(v.yuzde) > 0)
            .map(([k, v]) => [k, {
              aktif: v.aktif !== false,
              oran: Number(v.yuzde) / 100,
              tur: v.tur === 'satir' ? 'satir' : 'dahil',
              brut: !!v.brut,
              etiket: v.etiket || '',
            }])
        ),
        genel: taslak.genel
          .filter((g) => Number(g.yuzde) > 0)
          .map((g, i) => ({
            key: g.key || `fee_${i + 1}`,
            etiket: g.etiket || 'Service fee',
            oran: Number(g.yuzde) / 100,
            brut: !!g.brut,
          })),
      };
      const res = await fetch(`${API_BASE}/api/billing/fee-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: hdr(brandId),
        body: JSON.stringify({ settings }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Could not save settings (${res.status})`);
      setAyarlar({ mecralar: json.settings?.mecralar || {}, genel: json.settings?.genel || [] });
      setAyarPanel(false);
      setTaslak(null);
    } catch (e) {
      setPageError(e.message);
    } finally {
      setAyarKaydediliyor(false);
    }
  }, [brandId, taslak]);

  /* ── Izgara verisinin türetilmesi ────────────────────────────── */

  /** `${ay}|${platform}` → { total, campaigns, currency, state, … } */
  const hucreler = useMemo(() => {
    const out = {};
    for (const [anahtar, st] of Object.entries(cells)) {
      const campaigns = st.data?.campaigns || [];
      out[anahtar] = {
        state: st,
        currency: st.data?.currencies?.[0] || 'TRY',
        accounts: st.data?.accounts || [],
        // 'db' = platform API'sinin canlı penceresinin dışında, kayıtlı veriden
        source: st.data?.source || 'live',
        partial: !!st.data?.partial,  // hesapların bir kısmı hata verdi → tutar EKSİK
        failed: !!st.data?.failed,    // hiçbir hesap gelmedi → rakam yok
        accountErrors: st.data?.accountErrors || [],
        total: campaigns.reduce((s, c) => s + Number(c.spend || 0), 0),
        campaigns: [...campaigns].sort((a, b) => b.spend - a.spend),
      };
    }
    return out;
  }, [cells]);

  const bagliMecralar = useMemo(() => platforms.filter((p) => p.connected), [platforms]);
  const ucret = useCallback((key) => ayarlar.mecralar?.[key], [ayarlar]);

  /** Ay bazında toplamlar: ham harcama, mecra ücretleri, genel kalemler, genel toplam. */
  const toplamlar = useMemo(() => {
    const out = {};
    for (const ay of aylar) {
      let ham = 0;
      let mecraUcreti = 0;
      for (const p of bagliMecralar) {
        const t = hucreler[`${ay.key}|${p.key}`]?.total || 0;
        ham += t;
        mecraUcreti += ucretTutari(t, ucret(p.key));
      }
      const araToplam = ham + mecraUcreti;
      // Genel kalemler ARA TOPLAM üzerinden işler (mecra ücretleri dahil tutar).
      const genelKalemler = (ayarlar.genel || []).map((g) => ({
        ...g,
        tutar: ucretTutari(araToplam, { ...g, aktif: true }),
      }));
      const genelUcret = genelKalemler.reduce((s, g) => s + g.tutar, 0);
      out[ay.key] = { ham, mecraUcreti, araToplam, genelKalemler, genelUcret, genel: araToplam + genelUcret };
    }
    return out;
  }, [aylar, bagliMecralar, hucreler, ucret, ayarlar]);

  const anyLoading = platformsLoading || Object.values(cells).some((s) => s.loading);
  const ucretliMecraVar = bagliMecralar.some((p) => ucret(p.key)?.aktif);

  const yabanciParalar = useMemo(() => {
    const set = new Set();
    for (const st of Object.values(cells)) {
      for (const cur of st.data?.currencies || []) if (cur && cur !== 'TRY') set.add(cur);
    }
    return [...set];
  }, [cells]);

  /* CSV — ızgaranın birebir kopyası (Excel'e yapıştırmak için) */
  const downloadCsv = useCallback(() => {
    const satir = (hucreListesi) => hucreListesi.join(';');
    const bicim = (n) => Number(n || 0).toFixed(2).replace('.', ',');
    const lines = [satir(['Line item', ...aylar.map((a) => a.uzun)])];

    for (const p of bagliMecralar) {
      const u = ucret(p.key);
      const ham = (a) => hucreler[`${a.key}|${p.key}`]?.total || 0;
      lines.push(satir([
        (PLATFORM_BASLIK[p.key] || p.label) + (u?.aktif ? ` (${u.etiket || yuzdeYaz(u.oran)})` : ''),
        ...aylar.map((a) => bicim(u?.aktif && u.tur === 'dahil' ? ham(a) + ucretTutari(ham(a), u) : ham(a))),
      ]));
      if (u?.aktif && u.tur === 'satir') {
        lines.push(satir([
          `  ${u.etiket || yuzdeYaz(u.oran)}`,
          ...aylar.map((a) => bicim(ucretTutari(ham(a), u))),
        ]));
      }
    }

    lines.push('');
    lines.push(satir(['Media spend', ...aylar.map((a) => bicim(toplamlar[a.key]?.ham))]));
    if (ucretliMecraVar) {
      lines.push(satir(['Channel fees', ...aylar.map((a) => bicim(toplamlar[a.key]?.mecraUcreti))]));
      lines.push(satir(['Subtotal', ...aylar.map((a) => bicim(toplamlar[a.key]?.araToplam))]));
    }
    for (const [i, g] of (ayarlar.genel || []).entries()) {
      lines.push(satir([
        `${g.etiket} (${yuzdeYaz(g.oran)})`,
        ...aylar.map((a) => bicim(toplamlar[a.key]?.genelKalemler?.[i]?.tutar)),
      ]));
    }
    lines.push(satir(['GRAND TOTAL', ...aylar.map((a) => bicim(toplamlar[a.key]?.genel))]));

    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `spend-${(selectedBrand?.name || 'brand').replace(/\s+/g, '-')}-last${AY_SAYISI}months-${ayAnahtar(endYear, endMonth)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [aylar, bagliMecralar, hucreler, ucret, toplamlar, ucretliMecraVar, ayarlar, selectedBrand, endYear, endMonth]);

  const shiftMonth = (delta) => {
    const d = new Date(endYear, endMonth - 1 + delta, 1);
    setEndYear(d.getFullYear());
    setEndMonth(d.getMonth() + 1);
  };

  /* ── Hücre ───────────────────────────────────────────────────── */

  const Hucre = ({ ay, platformKey, ucretSatiri }) => {
    const h = hucreler[`${ay.key}|${platformKey}`];
    const st = h?.state || {};
    const u = ucret(platformKey);
    const hamDeger = h?.total;
    /* Üç durum:
       · ucretSatiri            → o mecranın ücret tutarı (kendi satırı)
       · ücret 'dahil'          → harcama + ücret (fatura tutarı)
       · ücret yok / 'satir'    → HAM harcama */
    const deger = hamDeger == null
      ? hamDeger
      : ucretSatiri
        ? ucretTutari(hamDeger, u)
        : u?.aktif && u.tur === 'dahil'
          ? hamDeger + ucretTutari(hamDeger, u)
          : Number(hamDeger);
    const acilabilir = !ucretSatiri && (h?.campaigns?.length || 0) > 0;
    const secili = detay && detay.platformKey === platformKey && detay.ay === ay.key;

    return (
      <td
        onClick={acilabilir ? () => setDetay(secili ? null : { platformKey, ay: ay.key }) : undefined}
        title={acilabilir ? `${ay.uzun} · ${h.campaigns.length} campaigns — click for breakdown` : undefined}
        className={`border-l border-slate-100 px-3 py-1.5 text-right tabular-nums
          ${ucretSatiri ? 'text-slate-500' : 'font-semibold text-slate-900'}
          ${acilabilir ? 'cursor-pointer hover:bg-indigo-50' : ''}
          ${secili ? 'bg-indigo-100 ring-1 ring-inset ring-indigo-300' : ''}`}
      >
        {st.loading ? (
          <span className="text-slate-300">…</span>
        ) : st.error || h?.failed ? (
          // Rakamı 0 diye göstermek yerine açıkça hata de — faturada sessiz sıfır,
          // aylarca harcamayı yok saymaya kadar gidiyor.
          <span
            className="font-normal text-rose-500"
            title={st.error || h.accountErrors.map((a) => `${a.name}: ${a.error}`).join('\n')}
          >
            error
          </span>
        ) : (
          <span
            className={h?.source === 'db' ? 'underline decoration-dotted decoration-slate-300 underline-offset-4' : undefined}
            title={h?.source === 'db' ? 'This month is outside the platform API\'s live window — read from stored daily data' : undefined}
          >
            {deger ? num(deger) : <span className="font-normal text-slate-300">0.00</span>}
            {h?.partial && (
              <span
                className="ml-1 text-amber-600"
                title={`INCOMPLETE — some accounts could not be loaded:\n${h.accountErrors.map((a) => `${a.name}: ${a.error}`).join('\n')}`}
              >
                !
              </span>
            )}
          </span>
        )}
      </td>
    );
  };

  /* ── Render ──────────────────────────────────────────────────── */

  if (!brandId) {
    return <div className="p-8 text-slate-500">Select a brand from the menu to continue.</div>;
  }

  const yillar = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) yillar.push(y);

  const detayHucre = detay ? hucreler[`${detay.ay}|${detay.platformKey}`] : null;
  const detayAy = detay ? aylar.find((a) => a.key === detay.ay) : null;
  const detayMecra = detay ? bagliMecralar.find((p) => p.key === detay.platformKey) : null;

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6">
      {/* Başlık + dönem seçimi */}
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[12px] font-bold uppercase tracking-wider text-indigo-600">Spend Invoice</p>
          <h1 className="mt-1 text-[24px] font-bold tracking-tight text-slate-900">Monthly Media Spend</h1>
          <p className="mt-1 text-[13px] text-slate-500">
            {selectedBrand?.name || 'Selected brand'} · {aylar[0]?.uzun} – {aylar[aylar.length - 1]?.uzun} ·
            figures are pulled live from platform APIs
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
            <button onClick={() => shiftMonth(-1)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Previous month">
              <ChevronLeft size={16} />
            </button>
            <span className="px-1 text-[11px] uppercase tracking-wide text-slate-400">last month</span>
            <select
              value={endMonth}
              onChange={(e) => setEndMonth(Number(e.target.value))}
              className="rounded border-0 bg-transparent px-1.5 py-1 text-[13px] font-medium text-slate-800 outline-none"
            >
              {AY_ADLARI.map((a, i) => <option key={a} value={i + 1}>{a}</option>)}
            </select>
            <select
              value={endYear}
              onChange={(e) => setEndYear(Number(e.target.value))}
              className="rounded border-0 bg-transparent px-1.5 py-1 text-[13px] font-medium text-slate-800 outline-none"
            >
              {yillar.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => shiftMonth(1)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Next month">
              <ChevronRight size={16} />
            </button>
          </div>

          <button
            onClick={ayarPanel ? () => { setAyarPanel(false); setTaslak(null); } : panelAc}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-[13px] font-semibold
              ${ayarPanel
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            <Settings2 size={15} /> Fee settings
          </button>

          <button
            onClick={fetchAll}
            disabled={anyLoading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {anyLoading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Refresh
          </button>

          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Download size={15} /> CSV
          </button>
        </div>
      </header>

      {pageError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{pageError}</div>
      )}

      {/* ── Ücret / komisyon ayarları ───────────────────────────── */}
      {ayarPanel && taslak && (
        <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Percent size={15} className="text-indigo-600" />
            <h2 className="text-[14px] font-bold text-slate-900">Fees and commission rates</h2>
            <span className="text-[12px] text-slate-500">
              These settings apply only to <b>{selectedBrand?.name}</b>; nothing is added to channels without a rate.
            </span>
            <button
              onClick={() => { setAyarPanel(false); setTaslak(null); }}
              className="ml-auto rounded p-1 text-slate-400 hover:bg-white"
              title="Close"
            >
              <X size={15} />
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-indigo-100 bg-white">
            <table className="w-full min-w-[820px] text-[12.5px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-semibold">Channel</th>
                  <th className="px-3 py-2 font-semibold">Rate</th>
                  <th className="px-3 py-2 font-semibold">Display</th>
                  <th className="px-3 py-2 font-semibold">Calculation</th>
                  <th className="px-3 py-2 font-semibold">Line label</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {bagliMecralar.map((p) => {
                  const t = taslak.mecralar[p.key] || {};
                  return (
                    <tr key={p.key} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-2">
                          {PLATFORM_LOGO[p.key] && (
                            <img src={PLATFORM_LOGO[p.key]} alt={p.label} className="h-4 w-4 shrink-0 object-contain" />
                          )}
                          <span className="font-medium text-slate-800">{PLATFORM_BASLIK[p.key] || p.label}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="99"
                            value={t.yuzde ?? ''}
                            placeholder="0"
                            onChange={(e) => mecraAyarla(p.key, { yuzde: e.target.value === '' ? '' : Number(e.target.value) })}
                            className="w-20 rounded border border-slate-300 px-2 py-1 text-right tabular-nums outline-none focus:ring-1 focus:ring-indigo-400"
                          />
                          <span className="text-slate-400">%</span>
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={t.tur || 'dahil'}
                          onChange={(e) => mecraAyarla(p.key, { tur: e.target.value })}
                          className="rounded border border-slate-300 bg-white px-2 py-1 outline-none"
                        >
                          <option value="dahil">Included in amount</option>
                          <option value="satir">Separate line</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <label className="inline-flex items-center gap-1.5" title="Withholding logic: amount ÷ (1 − rate) − amount. When off, a plain multiplication.">
                          <input
                            type="checkbox"
                            checked={!!t.brut}
                            onChange={(e) => mecraAyarla(p.key, { brut: e.target.checked })}
                          />
                          <span className="text-slate-600">gross up</span>
                        </label>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={t.etiket ?? ''}
                          placeholder={`e.g. ${PLATFORM_BASLIK[p.key] || p.label} commission`}
                          maxLength={60}
                          onChange={(e) => mecraAyarla(p.key, { etiket: e.target.value })}
                          className="w-56 rounded border border-slate-300 px-2 py-1 outline-none focus:ring-1 focus:ring-indigo-400"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        {Number(t.yuzde) > 0 && (
                          <button
                            onClick={() => mecraSil(p.key)}
                            className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                            title="Remove this channel's fee"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Toplam üzerinden işleyen kalemler */}
          <div className="mt-3 rounded-lg border border-indigo-100 bg-white p-3">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[12.5px] font-semibold text-slate-800">Items on the total</span>
              <span className="text-[11.5px] text-slate-500">
                Applied to the subtotal (media spend + channel fees) — e.g. agency service fee, operating fee.
              </span>
              <button
                onClick={() => setTaslak((t) => ({
                  ...t,
                  genel: [...t.genel, { key: `fee_${t.genel.length + 1}`, etiket: '', yuzde: '', brut: false }],
                }))}
                disabled={taslak.genel.length >= 5}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-2 py-1 text-[12px] font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
              >
                <Plus size={13} /> Add item
              </button>
            </div>
            {taslak.genel.length === 0 ? (
              <p className="text-[12px] text-slate-400">No items yet.</p>
            ) : (
              <div className="space-y-2">
                {taslak.genel.map((g, i) => (
                  <div key={g.key || i} className="flex flex-wrap items-center gap-2">
                    <input
                      value={g.etiket}
                      placeholder="Item name — e.g. Operating fee"
                      maxLength={60}
                      onChange={(e) => setTaslak((t) => ({
                        ...t,
                        genel: t.genel.map((x, j) => (j === i ? { ...x, etiket: e.target.value } : x)),
                      }))}
                      className="w-64 rounded border border-slate-300 px-2 py-1 text-[12.5px] outline-none focus:ring-1 focus:ring-indigo-400"
                    />
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="99"
                        value={g.yuzde}
                        placeholder="0"
                        onChange={(e) => setTaslak((t) => ({
                          ...t,
                          genel: t.genel.map((x, j) => (j === i ? { ...x, yuzde: e.target.value === '' ? '' : Number(e.target.value) } : x)),
                        }))}
                        className="w-20 rounded border border-slate-300 px-2 py-1 text-right text-[12.5px] tabular-nums outline-none focus:ring-1 focus:ring-indigo-400"
                      />
                      <span className="text-[12.5px] text-slate-400">%</span>
                    </span>
                    <label className="inline-flex items-center gap-1.5 text-[12.5px]" title="Withholding logic: amount ÷ (1 − rate) − amount.">
                      <input
                        type="checkbox"
                        checked={!!g.brut}
                        onChange={(e) => setTaslak((t) => ({
                          ...t,
                          genel: t.genel.map((x, j) => (j === i ? { ...x, brut: e.target.checked } : x)),
                        }))}
                      />
                      <span className="text-slate-600">gross up</span>
                    </label>
                    <button
                      onClick={() => setTaslak((t) => ({ ...t, genel: t.genel.filter((_, j) => j !== i) }))}
                      className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                      title="Remove item"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={ayarKaydet}
              disabled={ayarKaydediliyor}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
            >
              {ayarKaydediliyor ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save
            </button>
            <span className="text-[11.5px] text-slate-500">Examples:</span>
            {HAZIR_ORANLAR.map((h) => (
              <span key={h.etiket} className="rounded bg-white px-2 py-0.5 text-[11.5px] text-slate-500 ring-1 ring-slate-200">
                {h.etiket}
              </span>
            ))}
          </div>
        </div>
      )}

      {yabanciParalar.length > 0 && (
        <div className="mb-4 flex gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <p>
            This brand has ad accounts in currencies other than TRY ({yabanciParalar.join(', ')}).
            Amounts are shown in each account's own currency with NO conversion —
            don't use rows that add up different currencies as invoice amounts.
          </p>
        </div>
      )}

      {anyLoading && (
        <div className="mb-4 flex items-center gap-2 text-[12.5px] text-slate-400">
          <Loader2 size={14} className="animate-spin" />
          Pulling {AY_SAYISI} months live — columns fill from the newest month backwards.
        </div>
      )}

      {/* ── Izgara ──────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="sticky left-0 z-10 bg-slate-50 px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Line item
              </th>
              {aylar.map((a, i) => (
                <th
                  key={a.key}
                  className={`w-[130px] border-l border-slate-200 px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide
                    ${i === aylar.length - 1 ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500'}`}
                >
                  {a.kisa} {String(a.year).slice(2)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {bagliMecralar.map((p) => {
              const u = ucret(p.key);
              const paraBirimi = aylar.map((a) => hucreler[`${a.key}|${p.key}`]?.currency).find(Boolean) || 'TRY';
              return (
                <React.Fragment key={p.key}>
                  <tr className="border-t border-slate-200 hover:bg-slate-50/60">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2">
                      <span className="flex items-center gap-2">
                        {PLATFORM_LOGO[p.key] && (
                          <img src={PLATFORM_LOGO[p.key]} alt={p.label} className="h-[18px] w-[18px] shrink-0 rounded-sm object-contain" />
                        )}
                        <span className="text-[13.5px] font-bold text-slate-900">{PLATFORM_BASLIK[p.key] || p.label}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10.5px] text-slate-600">
                          {p.accounts.length} {p.accounts.length === 1 ? 'account' : 'accounts'}
                        </span>
                        {u?.aktif && (
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10.5px] ${
                              u.tur === 'dahil' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'
                            }`}
                            title={
                              u.brut
                                ? `Grossed up: amount ÷ ${(1 - u.oran).toLocaleString('en-US')} − amount`
                                : `Amount × ${u.oran.toLocaleString('en-US')}`
                            }
                          >
                            {u.etiket || yuzdeYaz(u.oran)} · {u.tur === 'dahil' ? 'included' : 'separate line'}
                          </span>
                        )}
                        {paraBirimi !== 'TRY' && (
                          <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10.5px] text-rose-700">{paraBirimi}</span>
                        )}
                      </span>
                    </td>
                    {aylar.map((a) => <Hucre key={a.key} ay={a} platformKey={p.key} />)}
                  </tr>

                  {u?.aktif && u.tur === 'satir' && (
                    <tr className="border-t border-slate-100 bg-slate-50/40">
                      <td className="sticky left-0 z-10 bg-slate-50/70 px-4 py-1">
                        <span className="flex items-center gap-2 pl-6 text-[12px] text-slate-500">
                          <span>└</span>
                          <span>{u.etiket || `${PLATFORM_BASLIK[p.key] || p.label} fee`} ({yuzdeYaz(u.oran)}{u.brut ? ', gross-up' : ''})</span>
                        </span>
                      </td>
                      {aylar.map((a) => <Hucre key={a.key} ay={a} platformKey={p.key} ucretSatiri />)}
                    </tr>
                  )}

                  {/* Tıklanan hücrenin kampanya kırılımı — satırın hemen altında */}
                  {detay && detay.platformKey === p.key && (
                    <tr className="border-t border-indigo-100 bg-indigo-50/40">
                      <td colSpan={aylar.length + 1} className="px-4 py-3">
                        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-indigo-800">
                          {detayAy?.uzun} · {PLATFORM_BASLIK[p.key] || detayMecra?.label}
                          <span className="font-normal text-indigo-500">
                            {detayHucre?.campaigns.length} campaigns · {money(detayHucre?.total, detayHucre?.currency)}
                          </span>
                          {u?.aktif && (
                            <span
                              className="rounded bg-white px-1.5 py-0.5 text-[10.5px] font-normal text-indigo-500"
                              title="Campaign breakdown is shown as it comes from the platform, EXCLUDING fees/commission."
                            >
                              campaign amounts exclude fees
                            </span>
                          )}
                          <button onClick={() => setDetay(null)} className="ml-auto rounded p-1 text-indigo-500 hover:bg-indigo-100" title="Close">
                            <X size={14} />
                          </button>
                        </div>
                        <table className="w-full text-[12px]">
                          <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-indigo-400">
                              <th className="pb-1.5 font-semibold">Campaign</th>
                              {(detayHucre?.accounts.length || 0) > 1 && <th className="pb-1.5 font-semibold">Account</th>}
                              <th className="pb-1.5 text-right font-semibold">Spend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detayHucre?.campaigns.map((c) => (
                              <tr key={`${c.accountId}-${c.id}`} className="border-t border-indigo-100">
                                <td className="py-1.5 pr-4 text-slate-700">{c.name}</td>
                                {(detayHucre?.accounts.length || 0) > 1 && (
                                  <td className="py-1.5 pr-4 text-slate-400">{c.accountName}</td>
                                )}
                                <td className="py-1.5 text-right tabular-nums text-slate-700">
                                  {money(c.spend, c.currency || detayHucre?.currency)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* ── Toplamlar ─────────────────────────────────────── */}
            <tr className="border-t-2 border-slate-300 bg-slate-50">
              <td className="sticky left-0 z-10 bg-slate-50 px-4 py-2 font-bold text-slate-900">
                Media Spend <span className="text-[11px] font-normal text-slate-500">(excluding fees)</span>
              </td>
              {aylar.map((a) => (
                <td key={a.key} className="border-l border-slate-100 px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                  {num(toplamlar[a.key]?.ham)}
                </td>
              ))}
            </tr>

            {ucretliMecraVar && (
              <>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td className="sticky left-0 z-10 bg-slate-50 px-4 py-1.5 text-[12px] text-slate-500">
                    └ channel fees / commissions
                  </td>
                  {aylar.map((a) => (
                    <td key={a.key} className="border-l border-slate-100 px-3 py-1.5 text-right text-[12px] tabular-nums text-slate-500">
                      {num(toplamlar[a.key]?.mecraUcreti)}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td className="sticky left-0 z-10 bg-slate-50 px-4 py-2 font-semibold text-slate-800">Subtotal</td>
                  {aylar.map((a) => (
                    <td key={a.key} className="border-l border-slate-100 px-3 py-2 text-right font-semibold tabular-nums text-slate-800">
                      {num(toplamlar[a.key]?.araToplam)}
                    </td>
                  ))}
                </tr>
              </>
            )}

            {(ayarlar.genel || []).map((g, i) => (
              <tr key={g.key || i} className="border-t border-slate-200 bg-amber-50/50">
                <td
                  className="sticky left-0 z-10 bg-amber-50/70 px-4 py-2 text-slate-700"
                  title={
                    g.brut
                      ? `Subtotal ÷ ${(1 - g.oran).toLocaleString('en-US')} − subtotal`
                      : `Subtotal × ${g.oran.toLocaleString('en-US')}`
                  }
                >
                  {g.etiket} <span className="text-[11px] text-slate-500">({yuzdeYaz(g.oran)}{g.brut ? ', gross-up' : ''})</span>
                </td>
                {aylar.map((a) => (
                  <td key={a.key} className="border-l border-slate-100 px-3 py-2 text-right tabular-nums text-slate-700">
                    {num(toplamlar[a.key]?.genelKalemler?.[i]?.tutar)}
                  </td>
                ))}
              </tr>
            ))}

            <tr className="border-t border-slate-300 bg-slate-100">
              <td className="sticky left-0 z-10 bg-slate-100 px-4 py-2 font-bold text-slate-900">
                Grand Total
                {(ucretliMecraVar || (ayarlar.genel || []).length > 0) && (
                  <span className="text-[11px] font-normal text-slate-500"> (including fees + commission)</span>
                )}
              </td>
              {aylar.map((a) => (
                <td key={a.key} className="border-l border-slate-200 px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                  {num(toplamlar[a.key]?.genel)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Ücret tanımlı değilse yol göster */}
      {!ucretliMecraVar && (ayarlar.genel || []).length === 0 && !ayarPanel && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-500">
          <Percent size={14} className="text-slate-400" />
          <span>
            No fees or commissions set for this brand — the table shows raw channel spend.
            To add an operating fee, agency commission or withholding, open
          </span>
          <button onClick={panelAc} className="font-semibold text-indigo-600 hover:underline">Fee settings</button>
        </div>
      )}

      {/* Bağlı olmayan mecralar */}
      {platforms.some((p) => !p.connected) && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-500">
          <Plug size={14} className="text-slate-400" />
          <span>Channels not connected for this brand:</span>
          {platforms.filter((p) => !p.connected).map((p) => (
            <span key={p.key} className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">{p.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}
